import { makeApi, Zodios, type ZodiosOptions } from '@zodios/core';
import { z } from 'zod';

const InitiateRequest = z
  .object({
    email: z.string().email(),
    method: z.enum(['FIDO2', 'MAGIC_LINK']),
    portal: z.string().optional(),
    otp: z
      .string()
      .regex(/^\d{6}$/)
      .optional(),
    redirectUri: z.string().url().optional(),
  })
  .passthrough();
const Fido2ChallengeResponse = z
  .object({
    session: z.string(),
    challengeType: z.literal('FIDO2'),
    fido2Options: z
      .object({
        challenge: z.string(),
        relyingPartyId: z.string().optional(),
        credentials: z
          .array(
            z.object({ id: z.string(), transports: z.array(z.string()).optional() }).passthrough(),
          )
          .optional(),
        timeout: z.number().int().optional(),
        userVerification: z.string().optional(),
      })
      .passthrough(),
  })
  .passthrough();
const MagicLinkSentResponse = z
  .object({ challengeType: z.literal('MAGIC_LINK_SENT') })
  .passthrough();
const ErrorResponse = z.object({ error: z.string() }).passthrough();
const RespondRequest = z
  .object({
    session: z.string().optional(),
    method: z.enum(['FIDO2', 'MAGIC_LINK']),
    portal: z.string().optional(),
    answer: z.object({}).partial().passthrough(),
  })
  .passthrough();
const TokenResponse = z
  .object({
    accessToken: z.string(),
    idToken: z.string(),
    refreshToken: z.string(),
    expiresIn: z.number().int(),
  })
  .passthrough();
const RefreshRequest = z
  .object({
    refreshToken: z.string(),
    portal: z.string().optional(),
    accessToken: z.string().optional(),
  })
  .passthrough();
const RefreshTokenResponse = z
  .object({ accessToken: z.string(), idToken: z.string(), expiresIn: z.number().int() })
  .passthrough();
const SsoInitiateResponse = z.object({ redirectUrl: z.string().url() }).passthrough();
const SsoExchangeRequest = z
  .object({
    code: z.string(),
    nonce: z.string(),
    portal: z.enum(['customer', 'client', 'admin']).optional(),
  })
  .passthrough();

export const schemas = {
  InitiateRequest,
  Fido2ChallengeResponse,
  MagicLinkSentResponse,
  ErrorResponse,
  RespondRequest,
  TokenResponse,
  RefreshRequest,
  RefreshTokenResponse,
  SsoInitiateResponse,
  SsoExchangeRequest,
};

const endpoints = makeApi([
  {
    method: 'post',
    path: '/auth/initiate',
    alias: 'postAuthinitiate',
    description: `Starts a FIDO2 or MAGIC_LINK authentication flow.
**FIDO2**: Returns an opaque session token and WebAuthn challenge options. The session token is HMAC-signed and bundles internal state; the frontend must pass it back verbatim to /auth/respond.
**MAGIC_LINK**: Triggers a magic link email to the user. The proxy performs two Cognito calls internally (InitiateAuth + RespondToAuthChallenge). No session is returned. The /auth/respond endpoint creates a fresh session when verifying the link. Requires &#x60;otp&#x60; (6-digit TOTP from authenticator app) and &#x60;redirectUri&#x60;.
`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: InitiateRequest,
      },
    ],
    response: z.union([Fido2ChallengeResponse, MagicLinkSentResponse]),
    errors: [
      {
        status: 400,
        description: `Invalid request`,
        schema: z.object({ error: z.string() }).passthrough(),
      },
      {
        status: 401,
        description: `Authentication failed`,
        schema: z.object({ error: z.string() }).passthrough(),
      },
    ],
  },
  {
    method: 'post',
    path: '/auth/refresh',
    alias: 'postAuthrefresh',
    description: `Exchanges a Cognito refresh token for new access and ID tokens. Uses Cognito&#x27;s REFRESH_TOKEN_AUTH flow internally. The refresh token itself is not rotated. Cognito returns only new access and ID tokens.
`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RefreshRequest,
      },
    ],
    response: RefreshTokenResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request or unknown portal`,
        schema: z.object({ error: z.string() }).passthrough(),
      },
      {
        status: 401,
        description: `Refresh token expired or revoked`,
        schema: z.object({ error: z.string() }).passthrough(),
      },
    ],
  },
  {
    method: 'post',
    path: '/auth/respond',
    alias: 'postAuthrespond',
    description: `Completes the authentication flow by submitting the challenge response.
**FIDO2**: &#x60;session&#x60; is required and must contain the opaque token from /auth/initiate. &#x60;answer&#x60; contains the FIDO2 credential assertion.
**MAGIC_LINK**: &#x60;session&#x60; is not required. The proxy extracts the username from the fragment, creates a fresh Cognito session via InitiateAuth, then verifies the fragment via RespondToAuthChallenge. &#x60;answer.fragment&#x60; contains the raw fragment identifier from the magic link URL.
`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RespondRequest,
      },
    ],
    response: TokenResponse,
    errors: [
      {
        status: 400,
        description: `Invalid request`,
        schema: z.object({ error: z.string() }).passthrough(),
      },
      {
        status: 401,
        description: `Authentication failed, invalid session, or session expired`,
        schema: z.object({ error: z.string() }).passthrough(),
      },
    ],
  },
  {
    method: 'get',
    path: '/auth/sso',
    alias: 'getAuthsso',
    description: `Returns a Cognito authorize URL for the specified SSO provider. The frontend redirects the browser to this URL with &#x60;window.location.href&#x60;. Cognito&#x27;s authorize endpoint redirects directly to the IdP (skipping the hosted UI provider selection screen) because identity_provider is specified.
`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'provider',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'callbackUrl',
        type: 'Query',
        schema: z.string().url(),
      },
      {
        name: 'portal',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'returnPath',
        type: 'Query',
        schema: z.string().optional().default('/'),
      },
    ],
    response: z.object({ redirectUrl: z.string().url() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `INVALID_REQUEST (missing provider), INVALID_PORTAL (unknown origin), INVALID_CALLBACK_URL (origin not allowed or path rejected), INVALID_PROVIDER (unknown provider name)
`,
        schema: z.object({ error: z.string() }).passthrough(),
      },
    ],
  },
  {
    method: 'get',
    path: '/auth/sso/callback',
    alias: 'getAuthssocallback',
    description: `Receives the authorization code from Cognito after IdP authentication. Validates the HMAC-sealed state, then redirects the browser to the frontend callback page with the code and OIDC nonce.
`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'code',
        type: 'Query',
        schema: z.string().optional(),
      },
      {
        name: 'state',
        type: 'Query',
        schema: z.string(),
      },
      {
        name: 'error',
        type: 'Query',
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 302,
        description: `Redirect to frontend callback page`,
        schema: z.void(),
      },
    ],
  },
  {
    method: 'post',
    path: '/auth/sso/exchange',
    alias: 'postAuthssoexchange',
    description: `Exchanges an OAuth2 authorization code for Cognito tokens (public client). Validates the OIDC nonce claim in the id_token matches the request nonce.
`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SsoExchangeRequest,
      },
    ],
    response: TokenResponse,
    errors: [
      {
        status: 401,
        description: `Authentication failed`,
        schema: z.object({ error: z.string() }).passthrough(),
      },
    ],
  },
]);

export const api = new Zodios(endpoints);

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options);
}
